import { useCallback, useEffect, useState } from 'react';
import { useCurrentRef } from 'src/react/_hooks/useCurrentRef';
import { useSearchSynced } from './useSearchSynced';

export type FilterRecord = Record<string, string | null>;

export type UseFilters = ReturnType<typeof useFilters>;

export type ImmutableMap<K, V> = Omit<Map<K, V>, 'set' | 'clear' | 'delete'>;

export const useFilters = () => {
  const [searchParams, setSearchParams] = useSearchSynced();
  const [selectedFilters, setSelectedFilters] = useState<ImmutableMap<string, string>>(new Map(searchParams.entries()));

  const [page, setPage] = useState(1);

  const resetPage = useCallback(() => setPage(1), []);

  const incrementPage = useCallback(() => setPage((prev) => prev + 1), []);

  const selectedFiltersCurrent = useCurrentRef(selectedFilters);

  const setFilters = useCallback(
    ({ filter, type }: { filter: FilterRecord; type: 'update' | 'clear' | 'reset' }) => {
      let newFilters = new Map<string, string>();

      setSelectedFilters((prev) => {
        switch (type) {
          case 'update':
            newFilters = new Map(prev);
            break;
          case 'clear':
            newFilters = new Map();
            break;
          case 'reset':
            newFilters = new Map(searchParams.entries());
            break;
        }

        for (const [key, value] of Object.entries(filter)) {
          if (value) {
            newFilters.set(key, value);
          } else {
            newFilters.delete(key);
          }
        }

        selectedFiltersCurrent.current = newFilters;

        return newFilters;
      });
    },
    [searchParams, selectedFiltersCurrent]
  );

  const applyChanges = useCallback(() => {
    const newSearchParams = new URLSearchParams(Array.from(selectedFiltersCurrent.current.entries()));

    setSearchParams(newSearchParams);
  }, [selectedFiltersCurrent, setSearchParams]);

  const isFilterApplied = useCallback(
    (filter: FilterRecord) => {
      for (const [key, value] of Object.entries(filter)) return value === searchParams.get(key);
    },
    [searchParams]
  );

  const clearFilters = useCallback(() => setFilters({ filter: {}, type: 'clear' }), [setFilters]);
  const removeUnsavedFilters = useCallback(() => setFilters({ filter: {}, type: 'reset' }), [setFilters]);
  const updateFilters = useCallback((filter: FilterRecord) => setFilters({ filter, type: 'update' }), [setFilters]);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(removeUnsavedFilters, [removeUnsavedFilters, searchParams]);

  return {
    selectedFilters,
    appliedFilters: searchParams,
    setFilters: updateFilters,
    removeUnsavedFilters,
    clearFilters,
    applyChanges,
    isFilterApplied,
    page,
    incrementPage,
    resetPage,
  };
};
